import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { ProductConfiguration } from "@omni/types/utils/productConfiguration";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useMemo, useState } from "react";

import { getProductConfiguration } from "@/utils/productConfiguration/productConfiguration.utils";

import { useECommerceProduct } from "../hooks/useECommerceProduct.hook";

type ProductConfigurationContextValue = {
    productConfiguration: ProductConfiguration<any> | undefined;
    configuration?: unknown;
    setConfiguration: React.Dispatch<React.SetStateAction<unknown | undefined>>;
    isConfigurationValid: boolean;
    setIsConfigurationValid: React.Dispatch<React.SetStateAction<boolean>>;
};

export type ProductConfigurationContextProviderProps = {};

export const ProductConfigurationContext = createContext<
    ProductConfigurationContextValue | undefined
>(undefined);

ProductConfigurationContext.displayName = "ProductConfigurationContext";

export const ProductConfigurationContextProvider = ({
    children
}: RequireChildrenFor<ProductConfigurationContextProviderProps>) => {
    const { sku, features, analytics } = useECommerceProduct();
    const [configuration, setConfiguration] = useState<unknown | undefined>(
        undefined
    );
    const [isConfigurationValid, setIsConfigurationValid] = useState<boolean>(
        !getProductConfiguration({
            sku,
            features,
            identifier: analytics.superCategoryCode
        })
    );

    const productConfiguration = useMemo(
        () =>
            getProductConfiguration({
                sku,
                features,
                identifier: analytics.superCategoryCode
            }),
        [sku, features, analytics.superCategoryCode]
    );

    return (
        <ProductConfigurationContext
            value={{
                productConfiguration,
                configuration,
                setConfiguration,
                isConfigurationValid,
                setIsConfigurationValid
            }}
        >
            {children}
        </ProductConfigurationContext>
    );
};

export const useProductConfigurationContext =
    (): ProductConfigurationContextValue => {
        const context = use<ProductConfigurationContextValue | undefined>(
            ProductConfigurationContext
        );
        if (!isDefined(context)) {
            throw new Error(
                `${ProductConfigurationContext.displayName} is undefined.`
            );
        }
        return context;
    };
